import { StatusModal } from '@/components/StatusModal';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';

type Cell = string | number;

type ParsedFile = {
  name: string;
  headers: string[];
  rows: Cell[][];
  skuCol: number;
  qtyCol: number;
};

type Diff = { sku: string; cz_qty: number; sk_qty: number; diff: number };

/** CSV i XLSX/XLS — SheetJS pozná formát sám podle obsahu, ne podle přípony. */
async function parseSpreadsheet(file: File): Promise<Cell[][]> {
  const buf = await file.arrayBuffer();
  const workbook = XLSX.read(buf, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return [];
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) return [];
  const rows = XLSX.utils.sheet_to_json<Cell[]>(sheet, { header: 1, defval: '' });
  return rows.filter((r) => Array.isArray(r) && r.some((c) => String(c).trim() !== ''));
}

function cellStr(v: Cell | undefined): string {
  return v === undefined ? '' : String(v).trim();
}

function cellNum(v: Cell | undefined): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const n = Number.parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function guessColumn(headers: string[], keywords: string[]): number {
  const lower = headers.map((h) => h.toLowerCase());
  for (const kw of keywords) {
    const idx = lower.findIndex((h) => h.includes(kw));
    if (idx !== -1) return idx;
  }
  return -1;
}

const selectCls =
  'border border-border rounded-md px-2 py-1.5 text-sm text-text bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30';

function FileBox({
  label,
  file,
  onFile,
  onColChange,
}: {
  label: string;
  file: ParsedFile | null;
  onFile: (f: File) => void;
  onColChange: (which: 'sku' | 'qty', idx: number) => void;
}) {
  return (
    <div className="bg-surface rounded-xl shadow-card p-5 flex-1 min-w-[280px]">
      <div className="text-sm font-bold text-text mb-2">{label}</div>
      <input
        type="file"
        accept=".csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
        }}
        className="text-sm text-text-secondary file:mr-3 file:rounded-md file:border-0 file:bg-action file:text-white file:px-3 file:py-1.5 file:text-sm file:font-semibold file:cursor-pointer cursor-pointer"
      />
      {file && (
        <div className="mt-3 space-y-2 text-sm">
          <div className="text-text-secondary">
            {file.name} — {file.rows.length} řádků
          </div>
          <label className="flex items-center gap-2">
            <span className="w-28 shrink-0 text-text-secondary">Sloupec SKU:</span>
            <select
              className={selectCls}
              value={file.skuCol}
              onChange={(e) => onColChange('sku', Number(e.target.value))}
            >
              <option value={-1}>— vyber —</option>
              {file.headers.map((h, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: pořadí sloupců je v rámci souboru stálé
                <option key={i} value={i}>
                  {h || `(sloupec ${i + 1})`}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2">
            <span className="w-28 shrink-0 text-text-secondary">Sloupec skladu:</span>
            <select
              className={selectCls}
              value={file.qtyCol}
              onChange={(e) => onColChange('qty', Number(e.target.value))}
            >
              <option value={-1}>— vyber —</option>
              {file.headers.map((h, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: pořadí sloupců je v rámci souboru stálé
                <option key={i} value={i}>
                  {h || `(sloupec ${i + 1})`}
                </option>
              ))}
            </select>
          </label>
          {file.skuCol >= 0 && file.qtyCol >= 0 && (
            <div className="text-text-muted">
              Náhled: {file.rows[0]?.[file.skuCol]} → {file.rows[0]?.[file.qtyCol]}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Rozdíly skladů — první nástroj: porovnání CZ vs. SK skladovosti podle
 * ručně nahraných exportů (kvůli rate limitu Shoptet API je to spolehlivější
 * než živé dotazy). Druhý tab (sklad vs. e-shop) je do budoucna.
 */
export function RozdilySkladuPage() {
  const [tab, setTab] = useState<'cz-sk' | 'sklad-eshop'>('cz-sk');
  const [cz, setCz] = useState<ParsedFile | null>(null);
  const [sk, setSk] = useState<ParsedFile | null>(null);
  const [diffs, setDiffs] = useState<Diff[] | null>(null);
  const [corrected, setCorrected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  async function onFile(file: File, which: 'cz' | 'sk') {
    let rows: Cell[][];
    try {
      rows = await parseSpreadsheet(file);
    } catch (e: unknown) {
      setToast({
        msg: `${file.name}: nepodařilo se přečíst (${e instanceof Error ? e.message : 'chyba'})`,
        kind: 'error',
      });
      return;
    }
    if (rows.length < 2) {
      setToast({ msg: `${file.name}: nepodařilo se přečíst žádná data`, kind: 'error' });
      return;
    }
    const headers = (rows[0] ?? []).map((h) => String(h));
    const body = rows.slice(1);
    const skuCol = guessColumn(headers, ['kód', 'kod', 'sku', 'code']);
    const qtyCol = guessColumn(headers, ['sklad', 'stock', 'množ', 'mnoz', 'qty']);
    const parsed: ParsedFile = { name: file.name, headers, rows: body, skuCol, qtyCol };
    if (which === 'cz') setCz(parsed);
    else setSk(parsed);
    setDiffs(null);
    setCorrected(new Set());
  }

  const readyToCompute =
    !!cz && !!sk && cz.skuCol >= 0 && cz.qtyCol >= 0 && sk.skuCol >= 0 && sk.qtyCol >= 0;

  function compute() {
    if (!cz || !sk || !readyToCompute) return;
    const czMap = new Map<string, number>();
    for (const r of cz.rows) {
      const skuVal = cellStr(r[cz.skuCol]);
      const qty = cellNum(r[cz.qtyCol]);
      if (skuVal && qty !== null) czMap.set(skuVal, qty);
    }
    const out: Diff[] = [];
    for (const r of sk.rows) {
      const skuVal = cellStr(r[sk.skuCol]);
      const qty = cellNum(r[sk.qtyCol]);
      if (!skuVal || qty === null) continue;
      const czQty = czMap.get(skuVal);
      if (czQty === undefined) continue;
      const diff = qty - czQty;
      if (diff !== 0) out.push({ sku: skuVal, cz_qty: czQty, sk_qty: qty, diff });
    }
    out.sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));
    setDiffs(out);
    setCorrected(new Set());
  }

  const toFix = useMemo(
    () => (diffs ?? []).filter((d) => !corrected.has(d.sku)),
    [diffs, corrected],
  );

  async function srovnat() {
    if (toFix.length === 0) return;
    setBusy(true);
    const BATCH = 30;
    let done = 0;
    let failed = 0;
    try {
      for (let i = 0; i < toFix.length; i += BATCH) {
        const batch = toFix.slice(i, i + BATCH);
        setProgress(`Srovnávám ${i + batch.length} / ${toFix.length}…`);
        const res = await apiFetch<{
          corrected: number;
          results: { sku: string; corrected: boolean }[];
        }>('/api/vyroba/eshop/srovnat-sklad-sk', {
          method: 'POST',
          body: {
            items: batch.map((d) => ({ sku: d.sku, target_qty: d.cz_qty, was_qty: d.sk_qty })),
          },
        });
        const ok = res.results.filter((r) => r.corrected).map((r) => r.sku);
        done += ok.length;
        failed += batch.length - ok.length;
        setCorrected((prev) => new Set([...prev, ...ok]));
      }
      setToast({
        msg: failed > 0 ? `Srovnáno ${done}, selhalo ${failed}` : `Srovnáno ${done} variant`,
        kind: failed > 0 ? 'error' : 'success',
      });
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Srovnání selhalo', kind: 'error' });
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  const tabCls = (active: boolean) =>
    `rounded-lg px-5 py-2.5 text-sm font-bold transition-colors cursor-pointer ${
      active
        ? 'bg-action text-white'
        : 'bg-bg border border-border text-text-secondary hover:border-text-muted'
    }`;

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Rozdíly skladů</h1>
        <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
          Kontrola nesrovnalostí mezi sklady a e-shopy.
        </p>
      </div>

      <div className="flex gap-2 mb-6">
        <button type="button" onClick={() => setTab('cz-sk')} className={tabCls(tab === 'cz-sk')}>
          CZ vs. SK e-shop
        </button>
        <button
          type="button"
          onClick={() => setTab('sklad-eshop')}
          className={tabCls(tab === 'sklad-eshop')}
        >
          Sklad vs. e-shop
        </button>
      </div>

      {tab === 'sklad-eshop' && (
        <p className="text-sm text-text-muted">Připravujeme — zatím tu není co zobrazit.</p>
      )}

      {tab === 'cz-sk' && (
        <>
          <div className="text-sm text-text bg-warning-light border-l-4 border-warning rounded-r-lg px-4 py-3 mb-5 max-w-3xl">
            Nahraj export skladovosti z CZ i SK Shoptet adminu (stejný formát, oba obsahují kód
            varianty a stav skladu) — kompletní export celého katalogu, ne jen výřez. Porovnání běží
            v prohlížeči — nic se neposílá nikam navíc, dokud nedáš „Srovnat sklad". Shoptet neumí
            zapsat sklad napřímo na přesnou hodnotu, jen relativní změnu — u té hrozí, že se sečte
            se Shoptetovou synchronizací skladů mezi e-shopy. Po srovnání zkontroluj v Shoptet
            adminu, že se to opravdu dorovnalo správně.
          </div>

          <div className="flex flex-wrap gap-4 mb-5">
            <FileBox
              label="CZ export"
              file={cz}
              onFile={(f) => onFile(f, 'cz')}
              onColChange={(which, idx) =>
                setCz((prev) =>
                  prev ? { ...prev, [which === 'sku' ? 'skuCol' : 'qtyCol']: idx } : prev,
                )
              }
            />
            <FileBox
              label="SK export"
              file={sk}
              onFile={(f) => onFile(f, 'sk')}
              onColChange={(which, idx) =>
                setSk((prev) =>
                  prev ? { ...prev, [which === 'sku' ? 'skuCol' : 'qtyCol']: idx } : prev,
                )
              }
            />
          </div>

          <button
            type="button"
            onClick={compute}
            disabled={!readyToCompute}
            className="bg-action hover:bg-action-dark disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold rounded-lg px-5 py-2.5 cursor-pointer transition-colors mb-6"
          >
            Porovnat
          </button>

          {diffs && (
            <>
              <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                <div className="text-sm text-text-secondary">
                  Nalezeno <strong className="text-text">{diffs.length}</strong> variant, kde se CZ
                  a SK skladovost liší.
                </div>
                {toFix.length > 0 && (
                  <button
                    type="button"
                    onClick={srovnat}
                    disabled={busy}
                    className="bg-danger hover:opacity-90 disabled:opacity-50 text-white text-sm font-bold rounded-lg px-5 py-2.5 cursor-pointer transition-opacity"
                  >
                    {busy ? (progress ?? 'Srovnávám…') : `Srovnat sklad (${toFix.length} variant)`}
                  </button>
                )}
              </div>

              {diffs.length === 0 ? (
                <p className="text-sm text-text-muted">Žádné rozdíly nenalezeny.</p>
              ) : (
                <div className="border border-border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-bg text-left text-xs font-semibold text-text-secondary">
                        <th className="px-3 py-2">SKU</th>
                        <th className="px-3 py-2">CZ sklad</th>
                        <th className="px-3 py-2">SK sklad</th>
                        <th className="px-3 py-2">Rozdíl (SK − CZ)</th>
                        <th className="px-3 py-2">Stav</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diffs.map((d) => (
                        <tr key={d.sku} className="border-t border-border-light">
                          <td className="px-3 py-2 font-mono text-text">{d.sku}</td>
                          <td className="px-3 py-2 text-text-secondary">{d.cz_qty}</td>
                          <td className="px-3 py-2 text-text-secondary">{d.sk_qty}</td>
                          <td
                            className={`px-3 py-2 font-semibold ${d.diff > 0 ? 'text-danger' : 'text-warning'}`}
                          >
                            {d.diff > 0 ? '+' : ''}
                            {d.diff}
                          </td>
                          <td className="px-3 py-2">
                            {corrected.has(d.sku) ? (
                              <span className="text-success font-semibold">✓ srovnáno</span>
                            ) : (
                              <span className="text-text-muted">čeká</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      {toast && (
        <StatusModal message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />
      )}
    </>
  );
}
